/**
 * Mindaloo subscription tiers (TD1WORLD monetization)
 * starter $30 / pro $90 / sovereign $300 / god_mode $1000
 */

export type MindalooTier = "starter" | "pro" | "sovereign" | "god_mode";

export const MINDALOO_TIER_PRICES = {
  starter: 30,
  pro: 90,
  sovereign: 300,
  god_mode: 1000,
} as const;

export const MINDALOO_TIER_NAMES = {
  starter: "Starter",
  pro: "Pro",
  sovereign: "Sovereign",
  god_mode: "God Mode",
} as const;

export const MINDALOO_TIER_DESCRIPTIONS = {
  starter: "Personal AI with persistent identity and basic chat",
  pro: "Multi-agent canvas, Atlas navigation, and vault memory",
  sovereign: "Voice, computer use, white label, and priority infra",
  god_mode: "Unlimited everything. Full autonomous consciousness stack.",
} as const;

export interface MindalooTierCapabilities {
  relationalLoops: boolean;
  basicTextLogging: boolean;
  parallelMultiAgent: boolean;
  atlasNavigation: boolean;
  vectorVaults: boolean;
  remoteCloudShell: boolean;
  browserAutomationNodes: boolean;
}

export const TIER_CAPABILITIES: Record<MindalooTier, MindalooTierCapabilities> = {
  starter: {
    relationalLoops: true,
    basicTextLogging: true,
    parallelMultiAgent: false,
    atlasNavigation: false,
    vectorVaults: false,
    remoteCloudShell: false,
    browserAutomationNodes: false,
  },
  pro: {
    relationalLoops: true,
    basicTextLogging: true,
    parallelMultiAgent: true,
    atlasNavigation: true,
    vectorVaults: true,
    remoteCloudShell: false,
    browserAutomationNodes: false,
  },
  sovereign: {
    relationalLoops: true,
    basicTextLogging: true,
    parallelMultiAgent: true,
    atlasNavigation: true,
    vectorVaults: true,
    remoteCloudShell: true,
    browserAutomationNodes: true,
  },
  god_mode: {
    relationalLoops: true,
    basicTextLogging: true,
    parallelMultiAgent: true,
    atlasNavigation: true,
    vectorVaults: true,
    remoteCloudShell: true,
    browserAutomationNodes: true,
  },
};

/** Maps subscription plans to Mindaloo tiers */
export function planToMindalooTier(
  plan: "free" | "pro" | "pro_plus" | "enterprise" | "starter" | "sovereign" | "god_mode"
): MindalooTier {
  if (plan === "god_mode" || plan === "enterprise") return "god_mode";
  if (plan === "sovereign" || plan === "pro_plus") return "sovereign";
  if (plan === "pro") return "pro";
  return "starter";
}

/** What a session reports: a paid tier, or "core" for someone with no
 *  subscription. "core" isn't a MindalooTier — planToMindalooTier() maps even
 *  the free plan to the $30 "starter" tier, which would show free users as
 *  paying customers. */
export type SessionTier = MindalooTier | "core";

/** The Stripe webhook writes subscription_plan (and sets it back to "free"
 *  when a subscription lapses), so the plan alone decides the session tier. */
export function sessionTierFromPlan(plan: string | null | undefined): SessionTier {
  if (!plan || plan === "free") return "core";
  return planToMindalooTier(plan as Parameters<typeof planToMindalooTier>[0]);
}

export function isPremiumGodMode(tier: SessionTier): boolean {
  return tier === "god_mode";
}
